import { BaseHit, Hit } from "instantsearch.js";
import defaultProductImg from "../../../img/default-image_420.png";
import Dictionary from "@/app/interfaces/common/Dictionary";
import { useEffect, useState } from "react";
import dataService from "../../services/dataService";
import SeoControl from "@/app/utils/seo/seoControl";
import { Box, Grid, Skeleton } from "@mui/material";
import BuilderIOSection from "../../builderio/components/builderIOSection";
import { BuilderIOSections } from "../../models/constants";
import { RootState } from "@/app/store/store";
import { useSelector } from "react-redux";
import { isSAPSource } from "@/app/utils/erpHelper";

interface GalleryHitProps extends Hit<BaseHit> {
  id: string;
  sku: string;
  name: string;
  manufacturername: string;
  pictureperwarehouse: Dictionary[];
}

interface GalleryHitsProps {
  warehouseCode: string | null;
  hit: GalleryHitProps;
  position: number;
  isAuthenticated: boolean;
  shouldLoad: boolean;
  onLoadComplete: () => void;
  hideSkeleton: boolean;
  isProductRecommenderTemplate: boolean;
}

const ProductGalleryHit = ({
  warehouseCode,
  hit,
  position,
  isAuthenticated,
  shouldLoad,
  onLoadComplete,
  hideSkeleton,
  isProductRecommenderTemplate,
}: GalleryHitsProps) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [customWindows, setCustomWindows] = useState<boolean>(false);
  const [customRenaissance, setRenaissance] = useState<boolean>(false);
  const [type, setType] = useState<string | null>(null);
  const [inventoryStatus, setInventoryStatus] = useState<string | null>(null);
  const [stockedColorRgb, setStockedColorRgb] = useState<string | null>(null);
  const [stockedColor, setStockedColor] = useState<string | null>(null);
  const [stockedMeasure, setStockedMeasure] = useState<string | null>(null);
  const [stockedVariantsCount, setStockedVariantsCount] = useState<number>(0);
  const [specialColorRgb, setSpecialColorRgb] = useState<string | null>(null);
  const [specialColor, setSpecialColor] = useState<string | null>(null);
  const [specialMeasure, setSpecialMeasure] = useState<string | null>(null);
  const [specialVariantsCount, setSpecialVariantsCount] = useState<number>(0);
  const [variantId, setVariantId] = useState<number | null>(null);
  const [mainPictureOverlay, setMainPictureOverlay] = useState<string | null>(
    null
  );
  const [customWindowPrice, setCustomWindowPrice] = useState<string | null>(
    null
  );
  const [productPrice, setProductPrice] = useState<string | null>(null);
  const [productUOM, setProductUOM] = useState<string | null>(null);
  const [sku, setProductSKU] = useState<string | null>(null);
  const [isGrouped, setIsGrouped] = useState<boolean>(false);
  const [hidePrice, setHidePrice] = useState<boolean>(false);
  let warehouseSource = useSelector(
    (state: RootState) => state.warehouseState?.selectedWarehouse?.source
  );

  let isSapWarehouse = isSAPSource(warehouseSource ?? "");

  useEffect(() => {
    if (shouldLoad && !loaded) {
      const timer = setTimeout(async () => {
        const response = await dataService.getProductDetails(
          hit.id,
          warehouseCode,
          false
        );

        // if (response.success === false) {
        //     setLoading(false);
        //     return;
        // }

        setType(response.data.type);
        setCustomWindows(response.data.customWindows);
        setRenaissance(response.data.customRenaissance);
        setInventoryStatus(response.data.inventoryStatus);
        setStockedColorRgb(response.data.stockedColorRgb);
        setStockedColor(response.data.stockedColor);
        setStockedMeasure(response.data.stockedMeasure);
        setStockedVariantsCount(response.data.stockedVariantsCount);
        setSpecialColorRgb(response.data.specialColorRgb);
        setSpecialColor(response.data.specialColor);
        setSpecialMeasure(response.data.specialMeasure);
        setSpecialVariantsCount(response.data.specialVariantsCount);
        setCustomWindowPrice(response.data.customWindowsPrice);
        setProductSKU(response.data.sku);
        setIsGrouped(response.data.isGrouped);
        setVariantId(response.data.variantDetails?.id ?? null);
        setHidePrice(response.data.hidePrice ?? false);

        if (
          isAuthenticated &&
          !response.data.customWindows &&
          !response.data.customRenaissance
        )
          dataService.fetchMinPrice(Number(hit.id)).then((priceResponse) => {
            setProductPrice(priceResponse?.data?.price ?? null);
            setProductUOM(priceResponse?.data?.uomFullName ?? null);
          });

        var variantDetails = response.data.variantDetails;
        if (variantDetails !== null && variantDetails.overlayRGB !== null)
          setMainPictureOverlay(variantDetails.overlayRGB);
        else if (response.data.overlayRGB != null)
          setMainPictureOverlay(response.data.overlayRGB);

        // setLoading(false);
        setLoaded(true);
        onLoadComplete();
      }, 0);
      return () => clearTimeout(timer);
    } else if (shouldLoad && loaded) onLoadComplete();
  }, [shouldLoad]);

  // const [loading, setLoading] = useState(true);

  // useEffect(() => {
  //     const timer = setTimeout(() => {
  //         setLoading(false);
  //     }, 500);
  //     return () => clearTimeout(timer);
  // }, []);

  var imageUrl = defaultProductImg;
  if (hit.pictureperwarehouse !== null && hit.pictureperwarehouse.length > 0) {
    var warehouseImage = hit.pictureperwarehouse.find(
      (x) =>
        x.item1 !== null &&
        x.item1.toLowerCase() === (warehouseCode ?? "").toLowerCase()
    );
    if (warehouseImage !== null && warehouseImage !== undefined)
      imageUrl = `${process.env.REACT_APP_CDN_URL}/${warehouseImage.item2}`;
    else {
      warehouseImage = hit.pictureperwarehouse.find((x) => x.item1 === "");
      if (warehouseImage !== null && warehouseImage !== undefined)
        imageUrl = `${process.env.REACT_APP_CDN_URL}/${warehouseImage.item2}`;
    }
  }

  let productUrlSubdirectory = isAuthenticated
    ? process.env.REACT_APP_STOREFRONT_SUBDIRECTORY
    : process.env.REACT_APP_SUBDIRECTORY;
  let productUrl = `${window.location.origin}/${productUrlSubdirectory}/product?id=${hit.id}`;
  if (variantId !== null) productUrl += `&associatedproduct=${variantId}`;

  let customWindowPriceLight = "$".repeat(5 - (customWindowPrice ?? "").length);
  let dollarPrice = null;
  let centPrice = null;
  if (productPrice != null) {
    const numericAmount = parseFloat(
      productPrice.toString().replace(/[$,]/g, "")
    );
    if (!isNaN(numericAmount)) {
      const [d, c] = numericAmount.toFixed(2).split(".");
      dollarPrice = d;
      centPrice = c;
    }
  }

  return (
    <>
      <SeoControl noindex={true} nofollow={true} />
      <Grid item md={12} sx={{ px: "4px" }}>
        <Box className={"productCard"}>
          {loaded || hideSkeleton ? (
            <BuilderIOSection
              section={
                isProductRecommenderTemplate
                  ? BuilderIOSections.RecommendedProductBox
                  : BuilderIOSections.PGPProductBox
              }
              preventLoading={true}
              data={{
                imageUrl: imageUrl,
                productName: hit.name,
                brand: hit.manufacturername ?? "",
                stockedVisible:
                  inventoryStatus === "Stocked" || stockedVariantsCount > 0,
                specialVisible:
                  inventoryStatus === "Special Order" ||
                  specialVariantsCount > 0,
                stockedRGB: stockedColorRgb,
                stockedMeasure: stockedMeasure,
                specialRGB: specialColorRgb,
                specialMeasure: specialMeasure,
                stockedMoreText:
                  stockedVariantsCount > 1
                    ? `+ ${stockedVariantsCount - 1} More ${
                        type == "Color" ? "Colors" : "Sizes"
                      }`
                    : stockedVariantsCount == 1
                    ? type == "Color"
                      ? `Available in ${stockedColor}`
                      : `Available in ${stockedMeasure}`
                    : null,
                specialMoreText:
                  specialVariantsCount > 1
                    ? `+ ${specialVariantsCount - 1} More ${
                        type == "Color" ? "Colors" : "Sizes"
                      }`
                    : specialVariantsCount == 1
                    ? type == "Color"
                      ? `Available in ${specialColor}`
                      : `Available in ${specialMeasure}`
                    : null,
                productUrl: productUrl,
                isGrouped: isGrouped,
                isAuthenticated: isAuthenticated,
                sku: isSapWarehouse ? hit.sapproduct : sku,
                customWindowPriceBold: customWindowPrice,
                customWindowPriceLight: customWindowPriceLight,
                dollarPrice: isAuthenticated ? dollarPrice : null,
                centPrice: isAuthenticated ? centPrice : null,
                productUOM: productUOM,
                inventoryStatus: inventoryStatus,
                customWindows: customWindows,
                customRenaissance: customRenaissance,
                hidePrice: hidePrice,
              }}
            />
          ) : (
            <Skeleton
              variant="rectangular"
              animation="wave"
              width={"100%"}
              height={300}
            />
          )}

          {/* <Typography sx={{ marginBottom: "10px" }}>
                                            {productPrice === null ? <></> : (
                                                <span>
                                                    <span style={{fontSize: "15px", fontWeight: "500", verticalAlign: "super"}}>
                                                        {productPrice[0]}
                                        <Typography sx={{ fontSize: "15px" }}>
                                            {inventoryStatus === null && productPrice !== null ? (
                                                <span>
                                                    Starting at
                                                </span>
                                            ) : <></>}
                                        </Typography>
                                        <Typography sx={{ marginBottom: "5px" }}>
                                            {productPrice === null ? <></> : (
                                                <span>
                                                    <span style={{fontSize: "15px", fontWeight: "500", verticalAlign: "super"}}>
                                                        {productPrice[0]}
                                                    </span>
                                                    <span style={{fontSize: "25px", fontWeight: "bolder", marginLeft: "2px", marginRight: "2px"}}>
                                                        {productPrice.slice(1).split(".")[0]}
                                                    </span>
                                                    <span style={{fontSize: "15px", fontWeight: "500", verticalAlign: "super"}}>
                                                        {productPrice.slice(1).split(".")[1]}
                                                    </span>
                                                    <span>
                                                        /
                                                    </span>
                                                    <span>
                                                        {productUOM}
                                                    </span>
                                                </span>
                                            )}
                                        </Typography>
                                                    </span>
                                                    <span style={{fontSize: "25px", fontWeight: "bolder", marginLeft: "2px", marginRight: "2px"}}>
                                                        {productPrice.slice(1).split(".")[0]}
                                                    </span>
                                                    <span style={{fontSize: "15px", fontWeight: "500", verticalAlign: "super"}}>
                                                        {productPrice.slice(1).split(".")[1]}
                                                    </span>
                                                    <span>
                                                        /
                                                    </span>
                                                    <span>
                                                        {productUOM}
                                                    </span>
                                                </span>
                                            )}
                                        </Typography> */}
        </Box>
      </Grid>
    </>
  );

  // return (
  //     <Grid item md={12} sx={{ px: 1 }}>
  //         <SeoIgnore></SeoIgnore>
  //         <Box className={"productCard"}>
  //             {loading ? (
  //                 <Skeleton variant="rectangular" animation="wave" width={"100%"} height={280} />
  //             ) : (
  //                 <div className='image-wrapper handCursor' onClick={() => RedirectToProduct(productUrl)}>
  //                     <ProductGalleryImage
  //                         src={imageUrl}
  //                         fallback={defaultProductImg}
  //                         alt="Product Gallery"
  //                         mainPictureOverlay={mainPictureOverlay} />
  //                 </div>
  //             )}
  //             <Box sx={{ mt: 2, minHeight: "280px" }}>
  //                 {loading ? (
  //                     <div className='bottomCardHolder'>
  //                         <div>
  //                             <Skeleton animation="wave" variant="text" width="80%" />
  //                             <Skeleton animation="wave" variant="text" width="80%" />
  //                             <Skeleton animation="wave" variant="text" width="60%" />
  //                             <Skeleton animation="wave" variant="text" width="60%" />
  //                         </div>
  //                         <Skeleton animation="wave" variant="rectangular" width={"100%"} height={50} />
  //                     </div>
  //                 ) : (
  //                     <div className='bottomCardHolder'>
  //                         <div className='bottomCardHolderText'>
  //                             <div>
  //                                 <Typography className='bottomCardHolderName handCursor' variant="h6" sx={{ fontSize: "16px", fontWeight: 700 }} onClick={() => RedirectToProduct(productUrl)}>
  //                                     <Highlight attribute="name" hit={hit} />
  //                                 </Typography>
  //                                 {hit.manufacturername === null || hit.manufacturername === undefined ? <></> : (
  //                                     <Typography className='bottomCardHolderBrand handCursor' variant="body2" onClick={() => RedirectToProduct(productUrl)}>
  //                                         <b>Brand:</b> <Highlight attribute="manufacturername" hit={hit} />
  //                                     </Typography>
  //                                 )}
  //                             </div>
  //                             <div>
  //                                 <Typography sx={{ fontSize: '14px' }}>
  //                                     {inventoryStatus === null ? <></> : (
  //                                         <>
  //                                             <span style={{ color: inventoryStatus === "Stocked" ? "green" : inventoryStatus === "Special Order" || customWindows || customRenaissance ? "red" : "" }}>{inventoryStatus}</span>
  //                                             <br />
  //                                             {customWindows || customRenaissance ? <></> : <span><b>SKU:</b> {hit.sku}</span>}
  //                                         </>
  //                                     )}
  //                                 </Typography>
  //                                 <Typography sx={{ fontSize: '14px' }}>
  //                                     {customWindows || customRenaissance ? (
  //                                         <>
  //                                             <span>{customWindowPrice}</span>
  //                                             <span style={{ color: "lightgray" }}>{"$".repeat((5 - (customWindowPrice ?? "").length))}</span>
  //                                         </>
  //                                     ) : <></>}
  //                                 </Typography>
  //                                 <Typography sx={{ fontSize: '14px' }}>
  //                                     {stockedVariantsCount > 0 && type === "Color" ? (
  //                                         <>
  //                                             <span style={{ color: "green" }}>Stocked</span>
  //                                             <span className='flex-start' style={{ whiteSpace: 'normal' }}>
  //                                                 <span style={{ height: "16px", width: "16px", border: "1px solid #d3d3d3", marginRight: "5px", backgroundColor: stockedColorRgb ?? "" }}></span>
  //                                                 {stockedVariantsCount > 1 ? (
  //                                                     <span><b> + {stockedVariantsCount - 1} More Colors</b></span>
  //                                                 ) : (
  //                                                     <span><b>Available in {stockedColor}</b></span>
  //                                                 )}
  //                                             </span>
  //                                         </>
  //                                     ) : <></>}
  //                                 </Typography>
  //                                 <Typography sx={{ fontSize: '14px' }}>
  //                                     {specialVariantsCount > 0 && type === "Color" ? (
  //                                         <>
  //                                             <span style={{ color: "red" }}>Special Order</span>
  //                                             <span className='flex-start' style={{ whiteSpace: 'normal' }}>
  //                                                 <span style={{ height: "16px", width: "16px", display: "inline-block", border: "1px solid #d3d3d3", marginRight: "5px", backgroundColor: specialColorRgb ?? "" }}></span>
  //                                                 {specialVariantsCount > 1 ? (
  //                                                     <span><b> + {specialVariantsCount - 1} More Colors</b></span>
  //                                                 ) : (
  //                                                     <span><b>Available in {specialColor}</b></span>
  //                                                 )}
  //                                             </span>
  //                                         </>
  //                                     ) : <></>}
  //                                 </Typography>
  //                                 <Typography>
  //                                     {stockedVariantsCount > 0 && type === "Measure" ? (
  //                                         <>
  //                                             <span style={{ color: "green" }}>Stocked</span>
  //                                             <br />
  //                                             <span className='measureContainer'>
  //                                                 <span style={{ border: "1px solid #d3d3d3" }}>{stockedMeasure}</span>
  //                                                 {stockedVariantsCount > 1 ? (
  //                                                     <span> + {stockedVariantsCount - 1} More Sizes</span>
  //                                                 ) : (
  //                                                     <span style={{fontWeight: "600", color: "#000"}}>Available in {stockedMeasure}</span>
  //                                                 )}
  //                                             </span>
  //                                         </>
  //                                     ) : <></>}
  //                                 </Typography>
  //                                 <Typography>
  //                                     {specialVariantsCount > 0 && type === "Measure" ? (
  //                                         <>
  //                                             <span style={{ color: "red" }}>Special Order</span>
  //                                             <br />
  //                                             <span className='measureContainer'>
  //                                                 <span style={{ border: "1px solid #d3d3d3" }}>{specialMeasure}</span>
  //                                                 {specialVariantsCount > 1 ? (
  //                                                     <span> + {specialVariantsCount - 1} More Sizes</span>
  //                                                 ) : (
  //                                                     <span style={{fontWeight: "600", color: "#000"}}>Available in {specialMeasure}</span>
  //                                                 )}
  //                                             </span>
  //                                         </>
  //                                     ) : <></>}
  //                                 </Typography>
  //                             </div>

  //                         </div>
  //                         <div style={{ height: "50px" }}>
  //                             <Link className='blueButtonOutline flex-center handCursor' to={productUrl}>VIEW PRODUCT</Link>
  //                         </div>
  //                     </div>
  //                 )}
  //             </Box>
  //             {/* <Box sx={{ mt: 2 }}>
  //                 {loading ? (
  //                 <Skeleton animation="wave" variant="rectangular" width={100} height={36} sx={{margin:"0 auto"}} />
  //                 ) : (
  //                 <Button variant="contained" color="primary">
  //                     dugme
  //                 </Button>
  //                 )}
  //             </Box> */}
  //         </Box>

  //     </Grid>
  // );
};

export default ProductGalleryHit;
